/**
 * Admin markets API layer (P1 Tier-A). Typed calls to the SHIPPED admin
 * marketplace router (/admin/marketplace/markets*), gated server-side on
 * `marketplace.market.manage` (list AND mutate share the slug). No backend
 * routes added. Mirrors the Pydantic schemas Market{Create,Update,Response,
 * ListResponse}.
 */
import { apiClient, apiFetch } from "@/lib/api-client";

/** lat/lng are Decimals serialised as JSON STRINGS (never floats). */
export interface AdminMarket {
  id: string;
  slug: string;
  name_ru: string;
  name_ky: string | null;
  city: string;
  address: string;
  lat: string;
  lng: string;
  entrance_count: number;
  overview_photo_s3_key: string | null;
}
export interface AdminMarketListResponse {
  markets: AdminMarket[];
  next_cursor: string | null;
}
export interface AdminMarketCreate {
  slug: string;
  name_ru: string;
  name_ky: string | null;
  city: string;
  address: string;
  lat: string;
  lng: string;
  /** Not surfaced in the admin UI (SCRUM-16x) — backend defaults to 1 when omitted. */
  entrance_count?: number;
}
export type AdminMarketUpdate = Partial<AdminMarketCreate>;

export const marketKeys = {
  list: (f: Record<string, unknown>) => ["admin", "markets", f] as const,
} as const;

export interface ListMarketsParams {
  cursor?: string | null;
  limit?: number;
  city?: string | null;
}
export function listMarkets(
  params: ListMarketsParams,
): Promise<AdminMarketListResponse> {
  const query = new URLSearchParams();
  if (params.limit) query.set("limit", String(params.limit));
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.city) query.set("city", params.city);
  const qs = query.toString();
  return apiClient.get<AdminMarketListResponse>(
    `/admin/marketplace/markets${qs ? `?${qs}` : ""}`,
  );
}
export function createMarket(body: AdminMarketCreate): Promise<AdminMarket> {
  return apiClient.post<AdminMarket, AdminMarketCreate>(
    "/admin/marketplace/markets",
    body,
  );
}
export function updateMarket(
  id: string,
  body: AdminMarketUpdate,
): Promise<AdminMarket> {
  return apiFetch<AdminMarket, AdminMarketUpdate>({
    method: "PATCH",
    path: `/admin/marketplace/markets/${id}`,
    body,
  });
}
export function deleteMarket(id: string): Promise<AdminMarket> {
  return apiClient.delete<AdminMarket>(`/admin/marketplace/markets/${id}`);
}
